import { isArray } from "../utils/isArray";
import { isString } from "../utils/isString";
import { RegExpParser } from "../utils/regexpParser";
import { isJsonString } from "./isJsonString";

const numberRegExp = /^[+-]?([0-9]+[.])?[0-9]+$/; // Integer & Float
const emailRegExp =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
const urlRegExp =
  /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)/;

type ValidationRules = Record<
  string,
  (_value: any, ..._args: any[]) => boolean | string
>;

export const validationFunctions: ValidationRules = {
  required: (value) => {
    const errorMsg = "{{label}} is required.";
    if (isString(value)) return Boolean(value.trim().length) || errorMsg;
    if (isArray(value)) return Boolean(value.length) || errorMsg;
    return Boolean(value) || errorMsg;
  },
  minLength: (value, min) => {
    if (!value) return true;
    return (
      value.trim().length >= Number(min) ||
      `{{label}} must be at least ${min} characters.`
    );
  },
  maxLength: (value, max) => {
    if (!value) return true;
    return (
      value.trim().length <= Number(max) ||
      `{{label}} can't be more than ${max} characters.`
    );
  },
  length: (value, min, max) => {
    if (!value) return true;
    if (!max) {
      return (
        value.length === Number(min) || `{{label}} must be ${min} characters.`
      );
    }

    if (value.length >= Number(min) && value.length <= Number(max)) return true;
    return `{{label}} must be between ${min} and ${max} characters.`;
  },
  startsWith: (value, searchString) => {
    if (!value) return true;
    return (
      value.startsWith(searchString) ||
      `{{label}} must start with ${searchString}.`
    );
  },
  endsWith: (value, searchString) => {
    if (!value) return true;
    return (
      value.endsWith(searchString) || `{{label}} must end with ${searchString}.`
    );
  },
  matches: (value, pattern) => {
    if (!value) return true;
    return (
      RegExpParser(pattern).test(value) ||
      `{{label}} must be formatted: ${pattern}`
    );
  },
  email: (value) => {
    if (!value) return true;
    return emailRegExp.test(value) || "{{label}} must be a valid email.";
  },
  url: (value) => {
    if (!value) return true;
    return urlRegExp.test(value) || "{{label}} must be a valid url.";
  },
  json: (value) => {
    if (!value) return true;
    return isJsonString(value) || "{{label}} must be a valid json.";
  },
  number: (value) => {
    if (!value) return true;
    return numberRegExp.test(value) || "{{label}} must be a valid number.";
  },
  min: (value, min) => {
    if (!value) return true;
    return Number(value) >= Number(min) || `{{label}} must be at least ${min}.`;
  },
  max: (value, max) => {
    if (!value) return true;
    return (
      Number(value) <= Number(max) ||
      `{{label}} must be less than or equal ${max}.`
    );
  },
  between: (value, min, max) => {
    if (!value) return true;
    if (Number(value) >= Number(min) && Number(value) <= Number(max))
      return true;
    return `{{label}} must be between ${min} and ${max}.`;
  },
  // TODO: Add messages when needed
  // dateAfter: (value, targetDate) =>
  //   value ? new Date(value).getTime() > new Date(targetDate).getTime() : true,
  // dateBefore: (value, targetDate) =>
  //   value ? new Date(value).getTime() < new Date(targetDate).getTime() : true,
  // dateBetween: (value, minTargetDate, maxTargetDate) =>
  //   value
  //     ? new Date(value).getTime() >= new Date(minTargetDate).getTime() &&
  //       new Date(value).getTime() <= new Date(maxTargetDate).getTime()
  //     : true,
};
